// src/pages/NotificationsPage.jsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

function NotificationsPage() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [filter, setFilter]               = useState('all'); // 'all' | 'unread' | 'orders'

  /* =========================================================
     CHARGEMENT
  ========================================================= */
  useEffect(() => {
    const token  = localStorage.getItem('auth_token');
    const userId = localStorage.getItem('user_id');

    if (!token || !userId) {
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    setIsAuthenticated(true);
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      const token  = localStorage.getItem('auth_token');
      const userId = localStorage.getItem('user_id');

      const { data } = await axios.get(
        `https://madadline.com/api/notifications/user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const list = Array.isArray(data) ? data : [];
      setNotifications(list);
    } catch (err) {
      console.error('Erreur notifications:', err);
      setError(err.response?.data?.message || 'Impossible de charger les notifications.');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     MARQUER COMME LU
  ========================================================= */
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `https://madadline.com/api/notifications/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.warn('Erreur API markAsRead:', err);
    }
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  const markAllAsRead = async () => {
    try {
      const token  = localStorage.getItem('auth_token');
      const userId = localStorage.getItem('user_id');
      await axios.put(
        `https://madadline.com/api/notifications/user/${userId}/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.warn('Erreur API markAllAsRead:', err);
    }
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  /* =========================================================
     FORMAT DATE
  ========================================================= */
  const formatDate = (dateStr) => {
    if (!dateStr) return 'récemment';
    const date = new Date(dateStr);
    const now  = new Date();
    const diff = (now - date) / 1000;

    if (diff < 60)      return "à l'instant";
    if (diff < 3600)    return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400)   return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 172800)  return 'hier';
    if (diff < 604800)  return `il y a ${Math.floor(diff / 86400)} jours`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  /* =========================================================
     ICÔNES SELON TYPE
  ========================================================= */
  const getIconSvg = (type) => {
    switch (type) {
      case 'order':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
        );
      case 'reply':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        );
      case 'message':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m2 6 10 7 10-7" />
          </svg>
        );
      case 'promo':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <circle cx="7" cy="7" r="1.5" fill="currentColor" />
          </svg>
        );
      case 'welcome':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 2v20M2 12h20" />
          </svg>
        );
      default:
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        );
    }
  };

  /* =========================================================
     FILTRES
  ========================================================= */
  const filteredNotifs = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'orders') return n.type === 'order';
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  /* =========================================================
     NON CONNECTÉ
  ========================================================= */
  if (!isAuthenticated) {
    return (
      <main className="notifications-page">
        <div className="notifications-page__empty">
          <div className="notifications-page__empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </div>
          <h2>Connectez-vous</h2>
          <p>Vous devez être connectée pour voir vos notifications.</p>
          <Link to="/login" className="btn btn-primary">Se connecter</Link>
        </div>
      </main>
    );
  }

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <main className="notifications-page">

      {/* ========== EN-TÊTE ========== */}
      <header className="notifications-page__head">
        <div className="notifications-page__head-inner">

          <button
            className="notifications-page__back"
            onClick={() => navigate(-1)}
            aria-label="Retour"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Retour
          </button>

          <div className="notifications-page__title-wrap">
            <span className="notifications-page__label">VOTRE ESPACE</span>
            <h1 className="notifications-page__title">Notifications</h1>
            {unreadCount > 0 && (
              <span className="notifications-page__count">
                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {unreadCount > 0 && (
            <button
              className="notifications-page__mark-all"
              onClick={markAllAsRead}
            >
              ✓ Tout marquer lu
            </button>
          )}
        </div>
      </header>

      {/* ========== CONTENU ========== */}
      <section className="notifications-page__content">

        {/* Filtres */}
        {notifications.length > 0 && (
          <div className="notifications-page__filters">
            <button
              className={`notifications-page__filter ${filter === 'all' ? 'is-active' : ''}`}
              onClick={() => setFilter('all')}
            >
              Toutes ({notifications.length})
            </button>
            <button
              className={`notifications-page__filter ${filter === 'unread' ? 'is-active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Non lues ({unreadCount})
            </button>
            <button
              className={`notifications-page__filter ${filter === 'orders' ? 'is-active' : ''}`}
              onClick={() => setFilter('orders')}
            >
              Commandes
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="notifications-page__loading">
            <div className="spinner" />
          </div>
        )}

        {/* Erreur */}
        {!loading && error && (
          <div className="notifications-page__error">
            <p>{error}</p>
            <button onClick={fetchNotifications} className="btn btn-primary">
              Réessayer
            </button>
          </div>
        )}

        {/* Vide */}
        {!loading && !error && filteredNotifs.length === 0 && (
          <div className="notifications-page__empty">
            <div className="notifications-page__empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <h3>
              {filter === 'unread' ? 'Aucune notification non lue' :
               filter === 'orders' ? 'Aucune notification de commande' :
               'Aucune notification'}
            </h3>
            <p>
              Vous serez prévenue ici des nouveautés, réassorts et mises à jour
              de vos commandes.
            </p>
            <Link to="/boutique" className="btn btn-primary">
              Découvrir la boutique
            </Link>
          </div>
        )}

        {/* Liste */}
        {!loading && !error && filteredNotifs.length > 0 && (
          <div className="notifications-page__list">
            {filteredNotifs.map(notif => (
              <article
                key={notif.id}
                className={`notif-item ${!notif.read ? 'is-unread' : ''}`}
                onClick={() => !notif.read && markAsRead(notif.id)}
              >
                <div className={`notif-item__icon notif-item__icon--${notif.type || 'default'}`}>
                  {getIconSvg(notif.type)}
                </div>

                <div className="notif-item__content">
                  <div className="notif-item__head">
                    <h3 className="notif-item__title">
                      {notif.title || 'Notification'}
                    </h3>
                    {!notif.read && <span className="notif-item__dot" />}
                  </div>

                  {notif.message && (
                    <p className="notif-item__message">{notif.message}</p>
                  )}

                  <span className="notif-item__time">
                    {formatDate(notif.created_at || notif.time)}
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}

      </section>
    </main>
  );
}

export default NotificationsPage;